import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Calendar, Clock, Activity, Syringe, AlertCircle, CheckCircle, Copy } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import useAutoSave from '@/hooks/useAutoSave';
import AutoSaveIndicator from '@/components/emr/AutoSaveIndicator';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentVisit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [copyingData, setCopyingData] = useState(false);

  // Get current date and time for defaults
  const now = new Date();
  const currentDate = format(now, 'yyyy-MM-dd');
  const currentTime = format(now, 'HH:mm');

  const initialFormData = {
    patient_id: patientId || '',
    visit_date: currentDate,
    visit_time: currentTime,
    
    // Vital Signs
    blood_pressure: '',
    temperature: '',
    oxygen_saturation: '',
    heart_rate: '',
    respiratory_rate: '',
    iv_access_location: '',
    
    // Treatment Info
    iv_cocktail: '',
    add_ons: [],
    tolerated_well: true,
    needle_size: '',
    attempts: 1,
    iv_site: '',
    questions_concerns: '',
    nurse_notes: ''
  };

  const {
    formData,
    updateField,
    setFormData,
    isSaving,
    lastSaved,
    clearDraft,
    resetForm
  } = useAutoSave('visit_documentation', initialFormData);

  // IV Cocktails list
  const ivCocktails = [
    'Myers Cocktail',
    'Hangover Drip',
    'Hydrate',
    'Athletic Drip',
    'Infusion Beauty',
    'Immune Drip',
    'Infusion Migraine'
  ];

  // Add-on vials list
  const addOnVials = [
    'B12 Energy Shot',
    'Vitamin D3 Shot',
    'Arginine Injection',
    'Lysine Injection',
    'Pyridoxine (B6) Injection',
    'Zinc Sulfate',
    'Tralement (Zinc/Copper/Manganese/Selenium)',
    'Ascorbic Acid (Vitamin C)',
    'Thiamine (B1)',
    'Calcium Gluconate',
    'Chromium (Chromic Chloride)',
    'Glutathione',
    'Taurine',
    'L-Carnitine',
    'Biotin (B7)',
    'Magnesium Sulfate',
    'B-Complex',
    'Amino Acid Blend',
    'Toradol (Ketorolac - Pain Relief)',
    'Zofran (Ondansetron - Anti-Nausea)',
    'Benadryl (Diphenhydramine)'
  ];

  const toggleAddOn = (vial) => {
    const currentAddOns = formData.add_ons || [];
    if (currentAddOns.includes(vial)) {
      updateField('add_ons', currentAddOns.filter(v => v !== vial));
    } else {
      updateField('add_ons', [...currentAddOns, vial]);
    }
  };

  // Fetch patient data when component loads
  useEffect(() => {
    if (patientId && user) {
      fetchPatientData();
    }
  }, [patientId, user]);

  const fetchPatientData = async () => {
    try {
      const response = await axios.get(`${API}/emr/patients/${patientId}`, { withCredentials: true });
      setPatientData(response.data);
    } catch (err) {
      console.error('Failed to load patient data:', err);
    }
  };

  const copyFromPreviousVisit = async () => {
    if (!patientId) {
      setError('Please select a patient first');
      return;
    }

    setCopyingData(true);
    try {
      // Try to get the most recent visit first
      const visitsResponse = await axios.get(`${API}/emr/patients/${patientId}/visits`, { withCredentials: true });
      const visits = visitsResponse.data;

      if (visits && visits.length > 0) {
        // Copy from most recent visit
        const lastVisit = visits[0];
        updateField('blood_pressure', lastVisit.blood_pressure || '');
        updateField('temperature', lastVisit.temperature || '');
        updateField('oxygen_saturation', lastVisit.oxygen_saturation || '');
        updateField('heart_rate', lastVisit.heart_rate || '');
        updateField('respiratory_rate', lastVisit.respiratory_rate || '');
        updateField('iv_access_location', lastVisit.iv_access_location || '');
        
        setSuccess('✅ Copied vital signs from most recent visit');
        setTimeout(() => setSuccess(''), 3000);
      } else if (patientData) {
        // If no previous visits, copy from patient intake form
        updateField('blood_pressure', patientData.blood_pressure || '');
        updateField('temperature', patientData.temperature || '');
        updateField('oxygen_saturation', patientData.oxygen_saturation || '');
        updateField('heart_rate', patientData.heart_rate || '');
        updateField('respiratory_rate', patientData.respiratory_rate || '');
        updateField('iv_access_location', patientData.iv_access_location || '');
        
        setSuccess('✅ Copied vital signs from patient intake form');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('No previous data found to copy');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      setError('Failed to load previous data');
      setTimeout(() => setError(''), 3000);
    } finally {
      setCopyingData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Combine date and time into ISO format
      const visitDateTime = new Date(`${formData.visit_date}T${formData.visit_time}`);
      
      const visitData = {
        ...formData,
        visit_date: visitDateTime.toISOString(),
        add_ons: formData.add_ons || []
      };

      // Remove the visit_time field as we've combined it into visit_date
      delete visitData.visit_time;

      const response = await axios.post(
        `${API}/emr/visits`,
        visitData,
        { withCredentials: true }
      );

      if (response.data) {
        setSuccess(true);
        clearDraft();
        setTimeout(() => {
          if (patientId) {
            navigate(`/emr/patient/${patientId}`);
          } else {
            navigate('/emr');
          }
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to document visit');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/');
    return null;
  }

  if (success) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-green-400 mb-2">Visit Documented!</h2>
          <p className="text-gray-400">Redirecting to patient chart...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-yellow-500/20 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/emr')}
                className="flex items-center space-x-2 text-gray-400 hover:text-yellow-400 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Dashboard</span>
              </button>
            </div>
            <AutoSaveIndicator isSaving={isSaving} lastSaved={lastSaved} />
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-gray-900 border border-yellow-500/20 rounded-2xl p-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-yellow-400 mb-2">Document Visit</h1>
            <p className="text-gray-400">Record patient treatment session with backdating capability</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Date and Time Selection */}
            <section className="bg-yellow-400/5 border border-yellow-400/20 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-yellow-400 mb-4 flex items-center">
                <Calendar className="w-6 h-6 mr-2" />
                Visit Date & Time
              </h2>
              <div className="bg-gray-800/50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-300 mb-2">
                  💡 <strong>Backdating Enabled:</strong> Select the actual date and time the visit occurred. 
                  Defaults to now, but you can change it to document visits from previous days.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Visit Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.visit_date}
                    max={currentDate}
                    onChange={(e) => updateField('visit_date', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Cannot select future dates</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Clock className="w-4 h-4 inline mr-2" />
                    Visit Time *
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.visit_time}
                    onChange={(e) => updateField('visit_time', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">24-hour or 12-hour format</p>
                </div>
              </div>
            </section>

            {/* Vital Signs */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-yellow-400 flex items-center">
                  <Activity className="w-6 h-6 mr-2" />
                  Vital Signs
                </h2>
                {patientId && (
                  <button
                    type="button"
                    onClick={copyFromPreviousVisit}
                    disabled={copyingData}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Copy className="w-4 h-4" />
                    <span>{copyingData ? 'Copying...' : 'Copy from Most Recent Assessment'}</span>
                  </button>
                )}
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-400">
                  💡 <strong>Time Saver:</strong> Click "Copy from Most Recent Assessment" to automatically populate vital signs from the patient's previous visit or initial intake. Update only the fields that have changed.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Blood Pressure</label>
                  <input
                    type="text"
                    value={formData.blood_pressure}
                    onChange={(e) => updateField('blood_pressure', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., 120/80"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Temperature</label>
                  <input
                    type="text"
                    value={formData.temperature}
                    onChange={(e) => updateField('temperature', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., 98.6°F"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Oxygen Saturation (O2)</label>
                  <input
                    type="text"
                    value={formData.oxygen_saturation}
                    onChange={(e) => updateField('oxygen_saturation', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., 98%"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Heart Rate</label>
                  <input
                    type="text"
                    value={formData.heart_rate}
                    onChange={(e) => updateField('heart_rate', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., 72 bpm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Respiratory Rate</label>
                  <input
                    type="text"
                    value={formData.respiratory_rate}
                    onChange={(e) => updateField('respiratory_rate', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., 16/min"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">IV Access Location</label>
                  <input
                    type="text"
                    value={formData.iv_access_location}
                    onChange={(e) => updateField('iv_access_location', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., Left antecubital, Right hand"
                  />
                </div>
              </div>
            </section>

            {/* Treatment Information */}
            <section>
              <h2 className="text-2xl font-bold text-yellow-400 mb-4 flex items-center">
                <Syringe className="w-6 h-6 mr-2" />
                Treatment Details
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">IV Cocktail Administered *</label>
                  <select
                    required
                    value={formData.iv_cocktail}
                    onChange={(e) => updateField('iv_cocktail', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  >
                    <option value="">Select IV Cocktail...</option>
                    {ivCocktails.map((cocktail) => (
                      <option key={cocktail} value={cocktail}>{cocktail}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">Add-On Vials (Select all that apply)</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {addOnVials.map((vial) => (
                      <label
                        key={vial}
                        className="flex items-center space-x-3 bg-gray-800 border border-gray-700 rounded-lg p-3 cursor-pointer hover:border-yellow-400 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={(formData.add_ons || []).includes(vial)}
                          onChange={() => toggleAddOn(vial)}
                          className="w-5 h-5 rounded border-gray-600 text-yellow-400 focus:ring-yellow-400 focus:ring-offset-gray-900"
                        />
                        <span className="text-white text-sm">{vial}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Needle Size</label>
                    <input
                      type="text"
                      value={formData.needle_size}
                      onChange={(e) => updateField('needle_size', e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                      placeholder="e.g., 22G"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Number of Attempts</label>
                    <select
                      value={formData.attempts}
                      onChange={(e) => updateField('attempts', parseInt(e.target.value))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    >
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">IV Site</label>
                    <input
                      type="text"
                      value={formData.iv_site}
                      onChange={(e) => updateField('iv_site', e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                      placeholder="e.g., AC, Hand"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3 bg-gray-800 border border-gray-700 rounded-lg p-4">
                  <input
                    type="checkbox"
                    id="tolerated"
                    checked={formData.tolerated_well}
                    onChange={(e) => updateField('tolerated_well', e.target.checked)}
                    className="w-5 h-5 rounded border-gray-600 text-yellow-400 focus:ring-yellow-400 focus:ring-offset-gray-900"
                  />
                  <label htmlFor="tolerated" className="text-white font-medium cursor-pointer">
                    Patient tolerated treatment well
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Patient Questions/Concerns</label>
                  <textarea
                    value={formData.questions_concerns}
                    onChange={(e) => updateField('questions_concerns', e.target.value)}
                    rows="3"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="Any questions or concerns the patient had..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Nurse Notes</label>
                  <textarea
                    value={formData.nurse_notes}
                    onChange={(e) => updateField('nurse_notes', e.target.value)}
                    rows="4"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="Additional clinical notes, observations, patient education provided..."
                  />
                </div>
              </div>
            </section>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-400 font-medium">Error Documenting Visit</p>
                  <p className="text-red-300 text-sm mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-700">
              <button
                type="button"
                onClick={() => navigate('/emr')}
                className="px-6 py-3 border border-gray-600 text-gray-300 font-medium rounded-lg hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Clear all fields?')) {
                      resetForm();
                    }
                  }}
                  className="px-6 py-3 border border-red-600 text-red-400 font-medium rounded-lg hover:bg-red-600/10 transition-colors"
                >
                  Clear Form
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all disabled:opacity-50 flex items-center space-x-2"
                >
                  <Save className="w-5 h-5" />
                  <span>{loading ? 'Documenting...' : 'Document Visit'}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DocumentVisit;
